import { useEffect } from "react";
import { SITE_URL } from "@/lib/schema";

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Sets document title, meta description, canonical, robots, Open Graph and JSON-LD
 * for the current route. Runs synchronously on mount since all SEO data is static/local
 * (no async fetch), so crawlers see final tags on first render.
 *
 * @param {Object} params
 * @param {string} params.title
 * @param {string} params.description
 * @param {string} params.path - route path starting with "/", used for canonical + OG url
 * @param {boolean} [params.isIndexable=true] - false => noindex,follow (phase 2 / not-yet-published pages)
 * @param {Array|Object} [params.jsonLd] - one schema object or an array of schema objects
 */
export function useSEO({ title, description, path = "/", isIndexable = true, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", isIndexable ? "index,follow" : "noindex,follow");
    setLink("canonical", `${SITE_URL}${path}`);

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", `${SITE_URL}${path}`);
    setMeta("property", "og:type", "website");

    const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : null;
    setJsonLd("page-jsonld", schemas);

    return () => {
      const s = document.getElementById("page-jsonld");
      if (s) s.remove();
    };
  }, [title, description, path, isIndexable, jsonLd]);
}
